import React from 'react';
import { useTranslation } from 'react-i18next';
import { RotateCcw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { clampWatermarkPosition, watermarkBoxSize, type WatermarkPosition } from '@/lib/watermark/watermarkPosition';
import { mapDragToWatermarkPosition } from '@/lib/watermark/mapDragToWatermarkPosition';
import { WATERMARK_DELAY_SECONDS } from '@/lib/watermark/resolveWatermarkStart';

export interface WatermarkDragHandleProps {
  /** Saved position, or null for the default. */
  position: WatermarkPosition | null;
  /** Preview composition aspect (width / height). */
  frameAspect: number;
  /** Draw a labeled outline (the playhead is before the watermark appears). */
  showPlaceholder?: boolean;
  onChange: (next: WatermarkPosition) => void;
  onReset: () => void;
}

const KEY_STEPS: Record<string, [number, number]> = {
  ArrowLeft: [-1, 0],
  ArrowRight: [1, 0],
  ArrowUp: [0, -1],
  ArrowDown: [0, 1],
};

/** The free-plan watermark's drag target over the preview. Must be a direct
 *  child of the composition-fit overlay: it measures its parent. */
export function WatermarkDragHandle({ position, frameAspect, showPlaceholder = false, onChange, onReset }: WatermarkDragHandleProps) {
  const { t } = useTranslation();
  const current = clampWatermarkPosition(position, frameAspect);
  const box = watermarkBoxSize(frameAspect);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    const rect = (e.currentTarget.parentElement as HTMLElement).getBoundingClientRect();
    const toFrac = (ev: { clientX: number; clientY: number }) => ({
      x: (ev.clientX - rect.left) / rect.width,
      y: (ev.clientY - rect.top) / rect.height,
    });
    const grab = toFrac(e);
    const offset = { x: grab.x - current.x, y: grab.y - current.y };
    let raf: number | null = null;
    let pending: PointerEvent | null = null;
    const apply = (ev: PointerEvent) => {
      const p = toFrac(ev);
      onChange(mapDragToWatermarkPosition({ x: p.x - offset.x, y: p.y - offset.y, frameAspect, snap: !(ev.ctrlKey || ev.metaKey) }));
    };
    const onMove = (ev: PointerEvent) => {
      pending = ev;
      if (raf != null) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        if (pending) apply(pending);
      });
    };
    const onUp = () => {
      if (raf != null) {
        cancelAnimationFrame(raf);
        raf = null;
      }
      if (pending) apply(pending);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const dir = KEY_STEPS[e.key];
    if (!dir) return;
    e.preventDefault();
    const step = e.shiftKey ? 0.05 : 0.01;
    onChange(mapDragToWatermarkPosition({ x: current.x + dir[0] * step, y: current.y + dir[1] * step, frameAspect, snap: false }));
  };

  return (
    <div
      data-testid="watermark-drag-handle"
      data-layer-ui
      role="button"
      tabIndex={0}
      aria-label={t('editorWatermark.handleLabel')}
      title={t('editorWatermark.handleLabel')}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
      className={cn(
        'group pointer-events-auto touch-none absolute z-30 cursor-move rounded-md border border-dashed outline-none',
        showPlaceholder
          ? 'border-white/70 bg-black/20'
          : 'border-transparent hover:border-primary/70 focus-visible:border-primary/70',
      )}
      style={{
        left: `${current.x * 100}%`,
        top: `${current.y * 100}%`,
        width: `${box.w * 100}%`,
        height: `${box.h * 100}%`,
        transform: 'translate(-50%, -50%)',
      }}
    >
      {showPlaceholder && (
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-[10px] font-medium text-white">
          {t('editorWatermark.appearsAt', { seconds: WATERMARK_DELAY_SECONDS })}
        </span>
      )}
      {position && (
        <button
          type="button"
          data-testid="watermark-reset"
          aria-label={t('editorWatermark.reset')}
          title={t('editorWatermark.reset')}
          className="absolute -right-2 -top-2 hidden rounded-full bg-background p-1 text-foreground shadow group-hover:block group-focus-within:block"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onReset();
          }}
        >
          <RotateCcw className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}
